import React, { useEffect } from 'react'
import type { ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { useAuthStore } from '@/stores/useAuthStore'
import { getCurrentUserId, getUserName, getUserRole } from '@/utils/jwt'
import { LANDING_URL } from '@/config/env'

interface PrivateRouteProps {
  children: ReactNode
}

/**
 * Guard for private routes
 * Redirects to LANDING_URL if there's no access token
 */
const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const token = useAuthStore((state) => state.accessToken)

  useEffect(() => {
    if (!token) {
      window.location.href = LANDING_URL
    }
  }, [token])

  // Sync Sentry user context whenever the token changes.
  // Covers both login flows (SSO + local), page-refresh token restores,
  // and logout (token → null clears the Sentry user).
  useEffect(() => {
    if (token) {
      Sentry.setUser({
        id: getCurrentUserId(token) ?? undefined,
        username: getUserName(token) ?? undefined,
        // `role` is a custom attribute — Sentry forwards it with every event
        role: getUserRole(token) ?? undefined,
      })
    } else {
      Sentry.setUser(null)
    }
  }, [token])

  return token ? <>{children}</> : null
}

export default PrivateRoute
